import React from 'react';
import { BarChart3Icon, DownloadIcon, VideoIcon, FootprintsIcon, ArrowRightIcon, LifeBuoyIcon } from 'lucide-react';
import { Button } from './Button';
export function Gen3Features() {
  const features = [{
    title: 'Thresholds / Hit Levels',
    description: 'View the official scoring thresholds and hit level settings for all divisions.',
    icon: <BarChart3Icon className="w-6 h-6" data-id="element-141" />,
    href: '#support-thresholds',
    external: false
  }, {
    title: 'Download Scoring Software',
    description: 'Get the latest Daedo scoring software for your TruScore system.',
    icon: <DownloadIcon className="w-6 h-6" data-id="element-142" />,
    href: '#support-software',
    external: false
  }, {
    title: 'Troubleshooting Video',
    description: 'Watch step-by-step troubleshooting guides for common equipment issues.',
    icon: <VideoIcon className="w-6 h-6" data-id="element-143" />,
    href: '#support-troubleshooting',
    external: false
  }, {
    title: 'E-Foot Usage Recommendations',
    description: 'Best practices and care instructions for your electronic foot gear.',
    icon: <FootprintsIcon className="w-6 h-6" data-id="element-144" />,
    href: '#support-efoot',
    external: false
  }];
  return <>
      <section className="py-20 bg-gradient-to-br from-blue-950 via-blue-900 to-indigo-950 text-white relative overflow-hidden mb-12" data-id="element-145">
        {/* Subtle glow effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" data-id="element-146"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" data-id="element-147"></div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" data-id="element-148">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMSIvPjwvc3ZnPg==')]" data-id="element-149"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-150">
          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-151">
            {/* Left Side: Main Message & CTA */}
            <div className="lg:w-5/12" data-id="element-152">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-400/15 text-blue-300 text-sm font-bold uppercase tracking-wider mb-6 border border-blue-400/25" data-id="element-153">
                <LifeBuoyIcon className="w-4 h-4" data-id="element-154" />
                Help Center
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight mb-6 leading-tight" data-id="element-155">
                Need Help With Your Daedo Setup?
              </h2>
              <div className="w-20 h-1 bg-blue-400 mb-6" data-id="element-156"></div>
              <p className="text-blue-100/70 text-lg mb-8 font-medium leading-relaxed" data-id="element-157">
                Get official support, software downloads, troubleshooting
                guides, and product recommendations to ensure your electronic
                scoring equipment performs flawlessly.
              </p>
              <a href="https://www.daedosupport.com/portal/en/kb/tkd-score" target="_blank" rel="noopener noreferrer" className="inline-block" data-id="element-158">
                <Button variant="primary" className="bg-blue-500 hover:bg-blue-600 focus:ring-blue-500 border-none text-lg px-8 py-4 shadow-lg shadow-blue-950/50" data-id="element-159">
                  Create Support Ticket
                  <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-160" />
                </Button>
              </a>
            </div>

            {/* Right Side: Resource Links */}
            <div className="lg:w-7/12 w-full" data-id="element-161">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-162">
                {features.map((feature, index) => <a key={index} href={feature.href} target={feature.external ? '_blank' : undefined} rel={feature.external ? 'noopener noreferrer' : undefined} className="group flex flex-col p-6 rounded-xl bg-white/[0.07] hover:bg-white/[0.12] border border-white/10 hover:border-blue-400/40 backdrop-blur-sm transition-all duration-300" data-id="element-163">
                    <div className="flex items-center gap-4 mb-4" data-id="element-164">
                      <div className="w-12 h-12 rounded-lg bg-blue-950/80 flex items-center justify-center text-blue-300 group-hover:bg-blue-500 group-hover:text-white transition-colors duration-300 border border-blue-800/50" data-id="element-165">
                        {feature.icon}
                      </div>
                      <h3 className="text-lg font-bold text-white leading-tight" data-id="element-166">
                        {feature.title}
                      </h3>
                    </div>
                    <p className="text-blue-200/50 text-sm flex-grow mb-4" data-id="element-167">
                      {feature.description}
                    </p>
                    <div className="flex items-center text-blue-300 font-bold text-sm uppercase tracking-wider group-hover:text-blue-200 mt-auto" data-id="element-168">
                      View Resource
                      <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-169" />
                    </div>
                  </a>)}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom edge treatment */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-400/30 to-transparent" data-id="element-170"></div>
      </section>

      {/* Spacing band between Support and Footer */}
      <div className="h-4 bg-gray-100" data-id="element-171"></div>
    </>;
}